import { useState, useEffect, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import { ChevronLeft, ChevronRight } from "lucide-react";

const slides = [
  {
    image: "/hero-banner.webp",
    title: "Premium Refurbished Laptops",
    subtitle: "Save up to 60% with 6-month warranty",
    cta: { label: "Shop Now", to: "/products" },
  },
  {
    image: "/hero-banner-2.webp",
    title: "Bulk Orders for Business",
    subtitle: "Best B2B prices with 100% GST billing",
    cta: { label: "Get Quote", to: "/bulk-order" },
  },
  {
    image: "/hero-banner-3.webp",
    title: "21-Point Quality Tested",
    subtitle: "Every device passes rigorous inspection",
    cta: { label: "Our Process", to: "/certification" },
  },
  {
    image: "/hero-banner-4.webp",
    title: "Sell Your Old Device",
    subtitle: "Instant cash. Free doorstep pickup",
    cta: { label: "Sell Now", to: "/sell" },
  },
];

const HeroBanner = () => {
  const [active, setActive] = useState(0);
  const [paused, setPaused] = useState(false);
  const touchRef = useRef<{ startX: number; startY: number } | null>(null);

  const next = useCallback(() => setActive((p) => (p + 1) % slides.length), []);
  const prev = useCallback(() => setActive((p) => (p - 1 + slides.length) % slides.length), []);

  useEffect(() => {
    if (paused) return;
    const id = setInterval(next, 5000);
    return () => clearInterval(id);
  }, [paused, next]);

  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    setPaused(true);
    touchRef.current = { startX: e.touches[0].clientX, startY: e.touches[0].clientY };
  }, []);

  const handleTouchEnd = useCallback((e: React.TouchEvent) => {
    if (!touchRef.current) return;
    const dx = e.changedTouches[0].clientX - touchRef.current.startX;
    const dy = e.changedTouches[0].clientY - touchRef.current.startY;
    if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy)) {
      if (dx < 0) next(); else prev();
    }
    touchRef.current = null;
    setTimeout(() => setPaused(false), 3000);
  }, [next, prev]);

  const slide = slides[active];

  return (
    <section
      className="relative w-full"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      {/* Mobile: clean card-style banner with rounded corners & margin */}
      <div
        className="relative w-full md:aspect-[21/8] overflow-hidden bg-muted md:rounded-none"
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
      >
        {/* Mobile: padded container for card effect */}
        <div className="md:hidden px-3 pt-2">
          <div className="relative w-full aspect-[16/9] rounded-2xl overflow-hidden shadow-md">
            {slides.map((s, i) => (
              <img
                key={i}
                src={s.image}
                alt={s.title}
                width={960}
                height={540}
                loading={i === 0 ? "eager" : "lazy"}
                fetchPriority={i === 0 ? "high" : undefined}
                decoding={i === 0 ? "sync" : "async"}
                className="absolute inset-0 w-full h-full object-cover"
                style={{
                  opacity: i === active ? 1 : 0,
                  transition: i === 0 && active === 0 ? "none" : "opacity 500ms ease-in-out",
                }}
              />
            ))}
            {/* Gradient */}
            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
            {/* Text */}
            <div className="absolute inset-0 flex flex-col justify-end p-4 pb-8">
              <h2
                key={`mh-${active}`}
                className="font-heading text-lg font-black text-white leading-tight mb-1 drop-shadow-lg animate-fade-in"
              >
                {slide.title}
              </h2>
              <p
                key={`mp-${active}`}
                className="text-[12px] text-white/85 font-medium mb-3 drop-shadow animate-fade-in"
                style={{ animationDelay: "60ms" }}
              >
                {slide.subtitle}
              </p>
              <Link
                key={`mc-${active}`}
                to={slide.cta.to}
                className="self-start inline-flex items-center bg-white text-foreground text-[12px] font-bold px-5 py-2 rounded-full shadow-lg active:scale-95 transition-transform animate-fade-in"
                style={{ animationDelay: "120ms" }}
              >
                {slide.cta.label}
              </Link>
            </div>
            {/* Dots */}
            <div className="absolute bottom-2.5 left-1/2 -translate-x-1/2 flex items-center gap-1.5">
              {slides.map((_, i) => (
                <button key={i} onClick={() => setActive(i)} aria-label={`Slide ${i + 1}`} className="p-0.5">
                  <span className={`rounded-full block transition-all duration-300 ${
                    i === active ? "w-5 h-[4px] bg-white shadow" : "w-[4px] h-[4px] bg-white/50"
                  }`} />
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Desktop: full-width */}
        <div className="hidden md:block relative w-full aspect-[21/8]">
          {slides.map((s, i) => (
            <img
              key={i}
              src={s.image}
              alt={s.title}
              width={960}
              height={512}
              loading={i === 0 ? "eager" : "lazy"}
              fetchPriority={i === 0 ? "high" : undefined}
              decoding={i === 0 ? "sync" : "async"}
              className="absolute inset-0 w-full h-full object-cover"
              style={{
                opacity: i === active ? 1 : 0,
                transition: i === 0 && active === 0 ? "none" : "opacity 600ms ease-in-out",
              }}
            />
          ))}
          <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/30 to-transparent" />
          <div className="absolute inset-0 flex flex-col justify-end p-8 lg:p-12">
            <div className="max-w-lg">
              <h2
                key={`dh-${active}`}
                className="font-heading text-2xl md:text-4xl font-black text-white leading-snug mb-2 drop-shadow-lg animate-fade-in"
              >
                {slide.title}
              </h2>
              <p
                key={`dp-${active}`}
                className="text-sm md:text-base text-white/90 font-medium mb-5 drop-shadow-md animate-fade-in"
                style={{ animationDelay: "80ms" }}
              >
                {slide.subtitle}
              </p>
              <Link
                key={`dc-${active}`}
                to={slide.cta.to}
                className="inline-flex items-center bg-white text-foreground text-sm font-bold px-7 py-3 rounded-full shadow-lg hover:bg-white/90 active:scale-95 transition-all animate-fade-in"
                style={{ animationDelay: "150ms" }}
              >
                {slide.cta.label}
              </Link>
            </div>
          </div>
          <button
            onClick={prev}
            className="absolute left-3 top-1/2 -translate-y-1/2 h-12 w-12 flex items-center justify-center rounded-full bg-white/20 backdrop-blur-sm text-white hover:bg-white/40 transition-colors"
            aria-label="Previous slide"
          >
            <ChevronLeft className="h-5 w-5" />
          </button>
          <button
            onClick={next}
            className="absolute right-3 top-1/2 -translate-y-1/2 h-12 w-12 flex items-center justify-center rounded-full bg-white/20 backdrop-blur-sm text-white hover:bg-white/40 transition-colors"
            aria-label="Next slide"
          >
            <ChevronRight className="h-5 w-5" />
          </button>
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex items-center gap-1.5 bg-black/25 backdrop-blur-sm rounded-full px-2.5 py-1.5">
            {slides.map((_, i) => (
              <button key={i} onClick={() => setActive(i)} aria-label={`Slide ${i + 1}`} className="p-0.5">
                <span className={`rounded-full block transition-all duration-300 ${
                  i === active ? "w-5 h-[5px] bg-white shadow-sm" : "w-[5px] h-[5px] bg-white/40"
                }`} />
              </button>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroBanner;
